import { Button } from "primereact/button";

import {
  actionTooltipProps,
  DisabledTooltip,
  ErrorState,
  ExportTableDialog,
  HubTable,
  Icon,
  TableSettingsDialog,
} from "@/ds";
import type { RoleSortField } from "@/modules/roles";
import { hubTableCartao, HubTableCartao } from "@/shared/components/hub-table-cartao";
import { ListaToolbar } from "@/shared/components/lista-toolbar";
import { FiltrosInline } from "../components/filtros-inline";
import { DeleteRoleDialog } from "./components/delete-role-dialog";
import { PermissionTags } from "./components/permission-tags";
import { RoleFormDialog } from "./components/role-form-dialog";
import { RoleInUseDialog } from "./components/role-in-use-dialog";
import {
  NEW_ROLE_LABEL,
  ROLES_ACTIONS_HEADER,
  ROLES_ACTIONS_KEY,
  ROLES_ACTIONS_WIDTH,
  ROLES_ERROR_MESSAGE,
  ROLES_EXPORT_LIST_NAME,
  ROLES_LOADING_LABEL,
  ROLES_PERMISSION_FILTER_KEY,
  ROLES_ROWS_PER_PAGE_LABEL,
  ROLES_SEARCH_PLACEHOLDER,
} from "./roles.constants";
import type { RolesColumnKey } from "./roles.constants";
import styles from "./roles.module.scss";
import type { RoleLinha, RolesViewModel } from "./types-roles";

const EDIT_TOOLTIP = actionTooltipProps("Editar", { event: "hover" });

/** Aba Perfis (Figma 4434:110756): busca, Permissões e ⋮ sobre a tabela; o lápis ou a linha abre a edição. */
export function RolesView({
  isMobile,
  search,
  buscar,
  filterColumns,
  filters,
  applyFilters,
  settings,
  exportView,
  notifySettingsApplied,
  colunas,
  items,
  total,
  isFirstLoad,
  status,
  errorMessage,
  reload,
  table,
  dataTableProps,
  hasSearch,
  hasFilters,
  limpar,
  catalogo,
  formulario,
  podeEditar,
  novo,
  editar,
  fecharFormulario,
  salvar,
  excluindo,
  bloqueado,
  excluir,
  cancelarExclusao,
  confirmarExclusao,
  fecharBloqueio,
  irParaUsuarios,
}: RolesViewModel) {
  const novoPerfil = (
    <Button
      type="button"
      className={styles.newButton}
      icon={<Icon name="plus" size={14} />}
      label={NEW_ROLE_LABEL}
      size={isMobile ? "small" : undefined}
      onClick={novo}
      data-testid="roles-new"
    />
  );

  return (
    <section className={styles.container} data-testid="roles-screen">
      <ListaToolbar
        testIdPrefix="roles"
        isMobile={isMobile}
        search={search}
        onSearchChange={buscar}
        searchPlaceholder={ROLES_SEARCH_PLACEHOLDER}
        searchWidth="13.625rem"
        inlineActions
        quickFilters={
          <FiltrosInline
            definitions={filterColumns}
            value={filters}
            onChange={applyFilters}
            testIdPrefix="roles"
            classNames={{ [ROLES_PERMISSION_FILTER_KEY]: styles.filtroPermissoes }}
          />
        }
        actionsEnd={podeEditar && !isMobile ? novoPerfil : undefined}
        onExport={exportView.open}
        onOpenSettings={settings.dialog.open}
      />

      {/* Mobile: o botão desce para a própria linha; na barra ele cobria o filtro de Permissões. */}
      {podeEditar && isMobile ? <div className={styles.novoMobile}>{novoPerfil}</div> : null}

      {exportView.isOpen ? (
        <ExportTableDialog
          listName={ROLES_EXPORT_LIST_NAME}
          isExporting={exportView.isExporting}
          onExport={exportView.run}
          onCancel={exportView.close}
        />
      ) : null}

      <TableSettingsDialog
        dialog={settings.dialog}
        testId="roles-table-settings"
        onApplied={notifySettingsApplied}
      />

      {formulario ? (
        // A chave descarta o formulário ao trocar de perfil.
        <RoleFormDialog
          key={formulario.role?.id ?? "novo"}
          role={formulario.role}
          permissions={catalogo.permissions}
          isLoadingPermissions={catalogo.isLoading}
          hasPermissionsError={catalogo.hasError}
          onSubmit={(values) => salvar(formulario.role, values)}
          onCancel={fecharFormulario}
        />
      ) : null}

      {excluindo ? (
        // A chave descarta o estado de envio ao trocar de perfil selecionado.
        <DeleteRoleDialog
          key={excluindo.id}
          role={excluindo}
          onConfirm={() => confirmarExclusao(excluindo)}
          onCancel={cancelarExclusao}
        />
      ) : null}

      {bloqueado ? (
        <RoleInUseDialog
          role={bloqueado}
          onClose={fecharBloqueio}
          onGoToUsers={() => irParaUsuarios(bloqueado)}
        />
      ) : null}

      <div className={styles.content}>
        <HubTableCartao<RoleLinha, RolesColumnKey, RoleSortField>
          value={items}
          totalRecords={total}
          table={table}
          settings={settings}
          status={status}
          errorMessage={errorMessage}
          loadingLabel={ROLES_LOADING_LABEL}
          onRowClick={editar}
          emptyNoun="perfil"
          emptyGender="m"
          emptyPlural="perfis"
          hasSearch={hasSearch}
          hasFilters={hasFilters}
          onClear={limpar}
          dataTableProps={dataTableProps}
          rowsPerPageLabel={ROLES_ROWS_PER_PAGE_LABEL}
          testId="roles"
          data-testid="roles-table-wrapper"
          data-first-load={isFirstLoad || undefined}
        >
          {colunas.map((coluna) => (
            <HubTable.Column<RoleLinha, RolesColumnKey>
              key={coluna.key}
              field={coluna.key}
              columnKey={coluna.key}
              header={coluna.header}
              style={coluna.width ? { width: coluna.width, minWidth: coluna.width } : undefined}
              body={(linha) =>
                coluna.celula === "tags" ? (
                  <PermissionTags permissions={linha.role.permissoes} />
                ) : (
                  linha.role[coluna.key]
                )
              }
            />
          ))}
          <HubTable.Column<RoleLinha, RolesColumnKey>
            field={ROLES_ACTIONS_KEY}
            columnKey={ROLES_ACTIONS_KEY}
            header={ROLES_ACTIONS_HEADER}
            sortable={false}
            hidden={!podeEditar}
            align="center"
            alignHeader="center"
            bodyClassName={hubTableCartao.actionCellClassName}
            headerClassName={hubTableCartao.actionCellClassName}
            style={{ width: ROLES_ACTIONS_WIDTH, minWidth: ROLES_ACTIONS_WIDTH }}
            body={(linha) => (
              <>
                <Button
                  type="button"
                  className={hubTableCartao.actionButtonClassName}
                  icon={<Icon name="pencil" size={14} />}
                  aria-label={linha.editLabel}
                  aria-haspopup="dialog"
                  tooltip={EDIT_TOOLTIP.content as string}
                  tooltipOptions={{ position: EDIT_TOOLTIP.position, event: "hover" }}
                  onClick={() => editar?.(linha)}
                  data-testid={linha.editTestId}
                  severity="secondary"
                  text
                />
                <DisabledTooltip content={linha.deleteTooltip}>
                  <Button
                    type="button"
                    className={hubTableCartao.actionButtonClassName}
                    icon={<Icon name="trash" size={14} />}
                    aria-label={linha.deleteLabel}
                    aria-haspopup="dialog"
                    disabled={linha.deleteDisabled}
                    onClick={() => excluir(linha)}
                    data-testid={linha.deleteTestId}
                    severity="secondary"
                    text
                  />
                </DisabledTooltip>
              </>
            )}
          />
          <HubTable.Error>
            <ErrorState
              message={ROLES_ERROR_MESSAGE}
              description={errorMessage ?? undefined}
              onRetry={reload}
              testId="roles-error"
            />
          </HubTable.Error>
        </HubTableCartao>
      </div>
    </section>
  );
}
